import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {CityService} from '@services/city.service';
import {AdService} from '@services/ad.service';
import {Favorite} from '@models/favorite';
import {User} from '@models/user';
import {EMPTY_USER} from '@models/empty';
import {AuthenticationService} from '@services/authentification.service';
import {DeviceDetectorService} from 'ngx-device-detector';

// Frais de notaire appliqués au prix d'achat pour le calcul du rendement brut
const NOTARY_RATE = 0.08;

@Component({
    selector: 'app-widget-rendement-typologie',
    templateUrl: './rendement-typologie.component.html',
    styleUrls: ['./rendement-typologie.component.scss']
})
export class RendementTypologieComponent implements OnInit {

    @Input() favorite: Favorite;
    @Input() preview: boolean;
    @Output() extChange = new EventEmitter();

    user: User = Object.assign({}, EMPTY_USER);

    notaryRate = NOTARY_RATE;

    iris = '';
    furnish = 0;

    echartsIntance = null;

    options = {
        tooltip: {
            trigger: 'item',
            confine: true,
            formatter: params => {
                const d = params.data;
                if (!d || !d.value) {
                    return params.name + '<br/>Données insuffisantes';
                }
                return '<strong>' + params.seriesName + ' - ' + params.name + '</strong><br/>'
                    + 'Rendement brut : <strong>' + d.value.toLocaleString('fr-FR', {minimumFractionDigits: 1, maximumFractionDigits: 1}) + ' %</strong><br/>'
                    + 'Surface moyenne : ' + Math.round(d.surface).toLocaleString('fr-FR') + ' m²<br/>'
                    + (d.bedrooms ? 'Colocation : ' + d.bedrooms + ' chambres<br/>' : '')
                    + 'Loyer moyen : ' + Math.round(d.rent).toLocaleString('fr-FR') + ' €/mois<br/>'
                    + 'Prix au m² : ' + Math.round(d.squarePrice).toLocaleString('fr-FR') + ' €/m²<br/>'
                    + 'Prix d\'achat : ' + Math.round(d.price).toLocaleString('fr-FR') + ' €<br/>'
                    + 'Frais de notaire (' + (NOTARY_RATE * 100) + ' %) : ' + Math.round(d.notary).toLocaleString('fr-FR') + ' €';
            }
        },
        legend: {show: true, orient: 'vertical', right: '0px', top: '15px', align: 'right', data: []},
        grid: {left: '20px', right: '4%', top: '40px', bottom: '15', containLabel: true},
        xAxis: {type: 'value', boundaryGap: [0, 0.01], min: 0, max: null, axisLabel: {formatter: '{value} %'}},
        yAxis: {type: 'category', data: []},
        color: ['#67C2EF', '#1277AA'],
        series: []
    };
    data = null;

    constructor(
        public cityService: CityService,
        public adService: AdService,
        public deviceService: DeviceDetectorService,
        public authenticationService: AuthenticationService) {
    }

    get hasColoc(): boolean {
        const coloc = this.favorite.ad_city.loyer_moyen_ville_coloc;
        return coloc != null && coloc.x0 > 0;
    }

    ngOnInit() {
        this.initData();
        this.extChange.subscribe(() => this.initData());
        this.user = this.authenticationService.currentUser;
    }

    initData() {
        this.iris = this.favorite.iris;
        this.furnish = this.favorite.furnish;
        if (this.furnish != 1 && this.furnish != 2 && this.furnish != 4) {
            this.furnish = 0;
        }
        this.options.xAxis.max = this.getAxisMax();
        this.setData();
    }

    edit(event, fields = '') {
        this.setData();
    }

    onChartInit(ec) {
        this.echartsIntance = ec;
    }

    /**
     * Suffixe de zone des clés de prix selon la sélection secteur / quartier
     */
    private zoneSuffix(iris: string): string {
        if (iris.length > 7) {
            return '_iris';
        }
        if (iris.length == 7) {
            return '_gq';
        }
        return '';
    }

    private furnishSuffix(furnish: number): string {
        if (furnish === 1) {
            return '_meuble';
        }
        if (furnish === 2) {
            return '_non_meuble';
        }
        return '';
    }

    private rentKey(type: string, piece: number, furnish: number): string {
        if (piece === 0) {
            return 'city.loyer_moyen_ville' + this.furnishSuffix(furnish) + '.' + type;
        }
        return 'city.loyer_moyen_ville_nb_piece' + this.furnishSuffix(furnish) + '[\'' + piece + '\']';
    }

    private priceKey(type: string, piece: number, iris: string): string {
        if (piece === 0) {
            return 'city.prix_commune' + this.zoneSuffix(iris) + '.' + type;
        }
        return 'city.prix_commune_nb_piece' + this.zoneSuffix(iris) + '.' + type + '[\'' + piece + '\']';
    }

    /**
     * Rendement brut = loyer annuel / (prix d'achat + frais de notaire)
     * Le loyer et la surface moyenne viennent des données de location,
     * le prix au m² des ventes notariales.
     * En colocation, le loyer est calculé sur le nombre de chambres (nb pièces - 1).
     */
    private getYield(type: string, piece: number, iris: string = null, furnish: number = null) {
        if (iris === null) {
            iris = this.iris;
        }
        if (furnish === null) {
            furnish = this.furnish;
        }
        const city = this.favorite.ad_city;
        const coloc = furnish === 4;

        // La colocation n'a de sens qu'à partir de 2 pièces (1 chambre + 1 pièce de vie)
        if (coloc && piece < 2) {
            return null;
        }

        // En colocation, la surface moyenne de la typologie vient des loyers nu et meublé confondus
        const rent = this.cityService.getRentValue(city, this.rentKey(type, piece, coloc ? 0 : furnish));
        const price = this.cityService.getPriceValue(city, this.priceKey(type, piece, iris), iris);

        if (!rent || !price || !rent.s_moy || !price.pM) {
            return null;
        }

        const surface = rent.s_moy;
        const bedrooms = coloc ? piece - 1 : 0;

        let monthlyRent;
        if (coloc) {
            const colocRent = this.cityService.getRentValue(city, 'city.loyer_moyen_ville.C');
            if (!colocRent || colocRent.x0 == 0) {
                return null;
            }
            monthlyRent = colocRent.x0 * bedrooms + colocRent.x1 * surface;
        } else {
            monthlyRent = rent.x0 + rent.x1 * surface;
        }

        if (monthlyRent <= 0) {
            return null;
        }

        const buyPrice = price.pM * surface;
        const notary = buyPrice * NOTARY_RATE;

        return {
            value: (monthlyRent * 12) / (buyPrice + notary) * 100,
            surface,
            bedrooms,
            rent: monthlyRent,
            squarePrice: price.pM,
            price: buyPrice,
            notary,
        };
    }

    /**
     * Échelle figée : le rendement le plus élevé toutes exploitations confondues,
     * pour que les barres restent comparables quand on change le menu Exploitation.
     */
    private getAxisMax(): number {
        let max = 0;
        [0, 1, 2, 4].forEach(furnish => {
            ['A', 'M'].forEach(type => {
                [0, 1, 2, 3, 4, 5].forEach(piece => {
                    const y = this.getYield(type, piece, this.iris, furnish);
                    if (y && y.value > max) {
                        max = y.value;
                    }
                });
            });
        });
        return max > 0 ? Math.ceil(max) : null;
    }

    setData() {
        const city = this.favorite.ad_city;
        const pieces = [0, 1, 2, 3, 4, 5];

        this.options.yAxis.data = [
            'Tout confondu',
            (this.deviceService.isMobile() ? '1 pièce' : '1 pièce / Studio'),
            '2 pièces',
            '3 pièces',
            '4 pièces',
            '5 pièces',
        ];
        this.options.legend.data = ['Appartement à ' + city.name, 'Maison à ' + city.name];

        this.data = {
            series: [
                {
                    name: 'Appartement à ' + city.name,
                    type: 'bar',
                    data: pieces.map(p => this.getYield('A', p)),
                },
                {
                    name: 'Maison à ' + city.name,
                    type: 'bar',
                    data: pieces.map(p => this.getYield('M', p)),
                },
            ]
        };

        if (this.deviceService.isMobile()) {
            this.options.grid.top = '25%';
            this.options.grid.right = '0px';
            this.options.legend.orient = 'horizontal';
            this.options.legend.right = 'auto';
            this.options.legend.align = 'auto';
        }

        if (this.echartsIntance) {
            this.echartsIntance.setOption(this.options);
        }
    }
}
